import { h, JSX } from 'preact'
import { useState } from 'preact/hooks'

import { MandatTableItem, sumAntall } from '../../shared/mandater'
import { Message } from '../../../../lib/intl/Message'
import { Geografi } from '../../../domain'
import { useWidgetUrls } from '../../../routes/widget'
import { withSectionErrorBoundary } from '../../shared/SectionErrorBoundary/SectionErrorBoundary'
import { EnkelMandatfordeling } from './EnkelMandatFordeling/EnkelMandatFordeling'
import { DetaljertMandatfordeling } from './DetaljertMandatfordeling/DetaljertMandatFordeling'

import styles from './Mandatfordeling.css'
import { TabPanel, Tabs } from '../../shared/Tabs/Tabs'
import { TabHeading } from '../../shared/Tabs/TabHeading'
import { idFrom } from '../../../api'

type TabIds = 'enkel' | 'detaljert'

type Props = {
  antall: number
  items: MandatTableItem[]
  valgtype: 'kommune' | 'fylke'
  geografi: Geografi
  initiallyOpen?: TabIds
  widgetFooter?: JSX.Element
}

function _Mandatfordeling(props: Props) {
  const { items, valgtype, antall, geografi, initiallyOpen, widgetFooter } = props
  const summedAntall = sumAntall(items)

  /* Antall er 0 eller undefined før resultatene kommer inn */
  if (!antall) {
    return null
  }

  // Mandat-mismatch skjer når stemmene ikke er talt opp.
  // Da vil vi vise størrelsen på/plassene i kommunestyre eller fylkestinget.
  const isMandatMismatch = antall !== summedAntall
  const type = geografi.type === 'Fylke' ? 'fylkesting' : 'kommunestyre'

  const [activeTab, setActiveTab] = useState<TabIds>(
    initiallyOpen === 'detaljert' && !isMandatMismatch ? 'detaljert' : 'enkel'
  )

  const { mandatfordelingUrl } = useWidgetUrls()
  const widgetUrl = mandatfordelingUrl(
    geografi,
    valgtype,
    activeTab === 'detaljert' ? 'detaljert' : 'enkel'
  )

  const navn = geografi.navn

  function onChange(idx) {
    const nextActiveTab = idx === 1 ? 'detaljert' : 'enkel'
    setActiveTab(nextActiveTab)
  }

  const itemsToRender = isMandatMismatch ? [generateFakeMandatTableItem(antall)] : items
  const description = isMandatMismatch ? `${type}-beskrivelse-null-tilstand` : `${type}-beskrivelse`

  return (
    <section
      class={styles.mandatfordeling}
      data-widget-url={widgetUrl}
      data-valgtype={valgtype}
      data-grunndata={idFrom(geografi)}
      data-modulnavn="mandatfordeling"
    >
      <Message as="h3" id={`${type}-tittel`} values={{ navn }} />
      <Message as="p" id={description} values={{ antall, navn }} />
      <Tabs
        onChange={onChange}
        headings={[
          <TabHeading icon="tab-overblikk">
            <Message id={`${type}-tab-tittel-enkel`} />
          </TabHeading>,
          <TabHeading icon="tab-detaljert" disabled={isMandatMismatch}>
            <Message id={`${type}-tab-tittel-detaljert`} />
          </TabHeading>
        ]}
      >
        <TabPanel hidden={activeTab === 'detaljert'}>
          <EnkelMandatfordeling
            items={itemsToRender}
            geografi={geografi}
            type={type}
            hideLegend={isMandatMismatch}
          />
        </TabPanel>
        <TabPanel hidden={activeTab !== 'detaljert'}>
          {isMandatMismatch ? null : (
            <DetaljertMandatfordeling type={type} items={items} geografi={geografi} />
          )}
        </TabPanel>
      </Tabs>
      {widgetFooter}
    </section>
  )
}

function generateFakeMandatTableItem(antall: number) {
  return {
    antall,
    navn: { nb: '', nn: '', sme: '' },
    endringAntall: 0,
    farge: '#989898',
    fargeSvak: '#e0e0e0',
    kortNavn: 'Uvisst'
  }
}

export const Mandatfordeling = withSectionErrorBoundary(_Mandatfordeling)
